import React from 'react';
import PropTypes from 'prop-types';
import { Typography } from '@wds/typography';
import { Grid } from '@wds/grid';
import { Box } from '@wds/box';
import { Button } from '@wds/button';
import { Tabs, Tab } from '@wds/tabs';
import { IconGear, IconUserFilled } from '@wds/icons';
import { List } from '@wds/list';
import { sanitizeString } from '@sm/utils';
import useStyles from './styles';
import AppDetailsMedia from './AppDetailsMedia';

const buildAppDetailsMediaFilesList = ({ details: { youtubeUrl = '', screenshots = [] }, name = '' }) => {
  const mediaList = [];

  if (youtubeUrl)
    mediaList.push({
      url: youtubeUrl,
      type: 'youtube',
      altText: name,
    });

  screenshots.map(s =>
    mediaList.push({
      url: s,
      type: 'image',
      altText: name,
    })
  );

  return mediaList;
};

const stripHttp = url => url.replace(/(^\w+:|^)\/\//, '');

const AppDetails = ({ appDetails }) => {
  const { appDetailsHeader, appDetailsHeaderLogo } = useStyles();
  const mediaFiles = buildAppDetailsMediaFilesList(appDetails);

  return (
    <Box>
      <Box className={appDetailsHeader} p={8}>
        <Grid noBleeds>
          <Grid.Item>
            <Grid noBleeds>
              <Grid.Item xs={3}>
                <Box className={appDetailsHeaderLogo} p={1}>
                  <img src={appDetails.logo} alt={`${appDetails.name} Logo`} width="100%" height="auto" />
                </Box>
              </Grid.Item>
              <Grid.Item>
                <Typography variant="hero1">{appDetails.name}</Typography>
                <Typography variant="bodySm">
                  by <a href={appDetails.details.websiteUrl}>{appDetails.details.publisher}</a>
                </Typography>
                <Box py={6}>
                  <Typography variant="body">{appDetails.tagline}</Typography>
                </Box>
                <If condition={!appDetails.installed}>
                  <Box display="inline" mr={3}>
                    <Button color="primary" href={appDetails.details.installUrl}>
                      Install
                    </Button>
                  </Box>
                </If>
                <If condition={appDetails.installed}>
                  <Button color="alt" href={appDetails.details.installUrl}>
                    Uninstall
                  </Button>
                </If>
              </Grid.Item>
            </Grid>
          </Grid.Item>
          <Grid.Item>
            <If condition={mediaFiles[0]}>
              <AppDetailsMedia media={mediaFiles[0]} maxWidth={310} />
            </If>
          </Grid.Item>
        </Grid>
      </Box>

      <If condition={mediaFiles.length > 1}>
        <Grid px={8} pt={6}>
          {mediaFiles.map(
            (media, index) =>
              index !== 0 &&
              index <= 5 && (
                <Grid.Item xs={3}>
                  <AppDetailsMedia media={media} />
                </Grid.Item>
              )
          )}
        </Grid>
      </If>
      <Box px={8} mb={4}>
        <ul>
          {appDetails.details.featureList.map(feature => (
            <li>{feature}</li>
          ))}
        </ul>
      </Box>
      <Box px={8}>
        <Tabs fullWidth color="transparent">
          <Tab title="Description" selected>
            <Box px={4}>
              <div
                dangerouslySetInnerHTML={{
                  __html: sanitizeString(appDetails.fullDescription),
                }}
              />
            </Box>
          </Tab>
          <Tab title="Requirements" data-testid="AppDetails__AppDirectoryRequirementsTab">
            <Box px={4} pt={2}>
              <If condition={appDetails.details.requirements}>
                <Box my={2}>
                  <Typography variant="cardTitle" component="h3" data-testid="AppDetails__AppDirectoryRequirements">
                    {`This app will require an additional ${appDetails.details.requirements.type} account:`}
                  </Typography>
                </Box>
                <List noRules>
                  <List.Item addOnLeft={<IconUserFilled color="light-muted" />}>
                    <a
                      href={`${appDetails.details.requirements.pricingUrl}en`}
                      target="_blank"
                      rel="noreferrer noopener"
                    >
                      <Typography className="wds-m-l-2" variant="link">
                        {appDetails.details.requirements.label}
                      </Typography>
                    </a>
                  </List.Item>
                </List>
                <If condition={appDetails.details.requirements.description}>
                  <p>{appDetails.details.requirements.description}</p>
                </If>
              </If>
              <If condition={appDetails.details.scopes}>
                <Box my={2}>
                  <Typography variant="cardTitle" component="h3">
                    This app will require access to:
                  </Typography>
                </Box>
                <List noRules>
                  {appDetails.details.scopes.map(scope => (
                    <List.Item key={scope} addOnLeft={<IconGear color="light-muted" />}>
                      <span>{scope}</span>
                    </List.Item>
                  ))}
                </List>
              </If>
            </Box>
          </Tab>
        </Tabs>
      </Box>
      <Grid px={8} py={4} mb={6}>
        <Grid.Item sm={3}>
          <Typography variant="cardTitle">Email</Typography>
          <Typography variant="body">
            <a href={`mailto:${appDetails.details.supportEmail}`}>{appDetails.details.supportEmail}</a>
          </Typography>
        </Grid.Item>
        <Grid.Item sm={3}>
          <Typography variant="cardTitle">Privacy</Typography>
          <Typography variant="body">
            <a href={appDetails.details.privacyPolicyUrl}>{stripHttp(appDetails.details.privacyPolicyUrl)}</a>
          </Typography>
        </Grid.Item>
        <Grid.Item sm={3}>
          <Typography variant="cardTitle">Terms of service</Typography>
          <Typography variant="body">
            <a href={appDetails.details.termsOfUseUrl}>{stripHttp(appDetails.details.termsOfUseUrl)}</a>
          </Typography>
        </Grid.Item>
      </Grid>
    </Box>
  );
};

AppDetails.propTypes = {
  appDetails: PropTypes.shape({
    id: PropTypes.string,
    isIntegration: PropTypes.bool,
    name: PropTypes.string,
    tagline: PropTypes.string,
    fullDescription: PropTypes.string,
    logo: PropTypes.string,
    installed: PropTypes.bool,
    details: PropTypes.shape({
      publisher: PropTypes.string,
      featureList: PropTypes.arrayOf(PropTypes.string),
      googleAnalyticsTrackingId: PropTypes.string,
      supportEmail: PropTypes.string,
      supportPhoneNumber: PropTypes.string,
      supportUrl: PropTypes.string,
      requirements: PropTypes.arrayOf(PropTypes.string),
      screenshots: PropTypes.arrayOf(PropTypes.string),
      privacyPolicyUrl: PropTypes.string,
      termsOfUseUrl: PropTypes.string,
      blogUrl: PropTypes.string,
      youtubeUrl: PropTypes.string,
      websiteUrl: PropTypes.string,
      installUrl: PropTypes.string,
      upgradeRequired: PropTypes.bool,
      optionalUpgrade: PropTypes.bool,
      scopes: PropTypes.arrayOf(PropTypes.string),
      link: PropTypes.string,
      linkType: PropTypes.string,
    }),
  }).isRequired,
};

export default AppDetails;
